import React, { useState } from 'react';
import clsx from 'clsx';
import { useGlobal, globalizationPath } from '@rcg/ui-react';
import { Img } from 'react-image';
import Dictionary from '../../utils/dictionary';
import useStyles from './useStyles';
import FlightDetailsModal from '../ReviewCard/FlightDetailsModal';
import { ReactComponent as DefaultIcon } from '../../assets/icons/icon-missing-carrier.svg';
import { emptyFunction } from '../../utils/common';
import useDictionary from '../../hooks/useDictionary';
import { NAVIGATION_PATHS, FLIGHT_SEARCH_TYPE } from '../../utils/constants';
import FareTooltip from '../FareTooltip';

const ReviewInfo = ({
  titlePreDate,
  flightIcon,
  date,
  startTime,
  endTime,
  numberOfStops,
  airlines,
  flightClass,
  departureCity,
  arrivalCity,
  totalDuration,
  flightNumber,
  numberOfPassangers,
  countStopsLabel,
  countStopsLabelBig,
  layoverDays,
  flightInfo,
  flightType,
  panelClass,
  isChangeFlights,
  isSummary,
  confirmationNumbers = null,
  isRefundable,
  isChange,
}) => {
  const {
    data: { lang, market },
  } = useGlobal();
  const classes = useStyles();
  const getDictionaryValue = useDictionary(Dictionary);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const currentUrl = window.location.href;

  const refundableLabel = isRefundable
    ? getDictionaryValue(Dictionary.airRefundableLabel.key)
    : getDictionaryValue(Dictionary.airNotRefundableLabel.key);

  const isBrowsePath = currentUrl.includes(NAVIGATION_PATHS.browse);
  const isDepartureFlight = flightType.type === FLIGHT_SEARCH_TYPE.DEPARTURE;

  const isSummaryPage =
    currentUrl.includes(NAVIGATION_PATHS.reviewFlight) ||
    currentUrl.includes(NAVIGATION_PATHS.browseSummary);

  const editFlightUrl = isBrowsePath
    ? `${urlPath}${
        isDepartureFlight ? NAVIGATION_PATHS.browseDeparture : NAVIGATION_PATHS.browseReturn
      }`
    : `${urlPath}${
        isDepartureFlight ? NAVIGATION_PATHS.resultsDeparture : NAVIGATION_PATHS.resultsReturn
      }`;

  const [openFlightDetailsModal, setOpenFlightDetailsModal] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);

  const cabinClassNames = {
    Y: getDictionaryValue(Dictionary.airClassYName.key),
    S: getDictionaryValue(Dictionary.airClassSName.key),
    C: getDictionaryValue(Dictionary.airClassCName.key),
    F: getDictionaryValue(Dictionary.airClassFName.key),
  };

  const handleOpenFlightDetailsModal = (info) => () => {
    setSelectedFlight(info);
    setOpenFlightDetailsModal(true);
  };
  const handleCloseFlightDetailsModal = () => {
    setSelectedFlight(null);
    setOpenFlightDetailsModal(false);
  };
  return (
    <>
      <div
        className={clsx(
          classes.cardFlightInfoBox,
          'cardFlightInfoBox',
          isSummary ? 'summary' : '',
        )}>
        <div className={classes.flightDateWrapper}>
          <div className={classes.iconAirlineAndDate}>
            <Img
              src={flightIcon}
              alt="flight-icon"
              className={classes.flightAirlineLogo}
              unloader={<DefaultIcon className={classes.flightAirlineLogo} />}
            />
            <span className={!panelClass ? classes.flightDate : classes.flightDatePanel}>
              {titlePreDate} — {date}
            </span>
          </div>
          {confirmationNumbers && (
            <div className={classes.confirmationNumber}>
              {confirmationNumbers.length > 1 ? (
                <>
                  <span>
                    {getDictionaryValue(Dictionary.airFlightConfirmationNumbersLabel.key)}{' '}
                  </span>
                  <span className="singleLine">{confirmationNumbers[0]}</span>
                  <span>, {confirmationNumbers[1]}</span>
                </>
              ) : (
                <>
                  <span>
                    <span>
                      {getDictionaryValue(Dictionary.airFlightConfirmationNumberLabel.key)}{' '}
                    </span>
                    <span className="singleLine">{confirmationNumbers[0]}</span>
                  </span>
                </>
              )}
            </div>
          )}

          <div className="details-actions">
            <span
              data-testid="open-flight-details"
              className={!panelClass ? classes.flightDetails : classes.flightDetailsPanel}
              role="button"
              onKeyDown={emptyFunction}
              tabIndex="0"
              onClick={handleOpenFlightDetailsModal({
                ...flightInfo,
                flightType,
              })}>
              {getDictionaryValue(Dictionary.airFlightDetailsModalLabel.key)}
            </span>
            {isSummaryPage && (
              <a className={classes.flightDetails} href={editFlightUrl}>
                {getDictionaryValue(Dictionary.airFlightDetailsEditlLabel.key)}
              </a>
            )}
          </div>
        </div>
        <div className={clsx(classes.flightInfo, 'flightInfo')}>
          <div
            className={clsx(
              classes.flightInfoWrapper,
              !panelClass ? classes.firstColumn : classes.firstColumnPanel,
            )}>
            <div className={classes.flightInfoPrimary}>
              {startTime} — {endTime}
              <span className={classes.layoverDaysReview}>
                &nbsp;
                {layoverDays}
              </span>
            </div>
            <div
              className={clsx(
                classes.flightInfoSecondary,
                classes.justifiedFlightInfo,
                classes.isBrowsePath,
                !panelClass ? classes.flightInfoDownLgHide : classes.flightInfoDownLgHidePanel,
              )}>
              <p>
                {airlines &&
                  airlines.indexOf(',') === -1 &&
                  !isChangeFlights &&
                  getDictionaryValue(Dictionary.airOperatorLabel.key)}{' '}
                {airlines}
                &nbsp;&nbsp;&nbsp;{!isChangeFlights ? '|' : '-'}
                &nbsp;&nbsp;&nbsp;
                {cabinClassNames[flightClass]}
              </p>
              {!isChange && (
                <>
                  <p>|</p>
                  <FareTooltip elementLabel={refundableLabel} />
                </>
              )}
            </div>
            <div
              className={clsx(
                classes.flightInfoSecondary,
                !panelClass ? classes.flightInfoDownLgShow : classes.flightInfoDownLgShowPanel,
              )}>
              <div>{`${departureCity} - ${totalDuration} - ${arrivalCity}`}</div>
              <div
                className={clsx(
                  classes.flightInfoSecondaryExtraInfo,
                  classes.justifiedFlightInfo,
                  classes.isBrowsePath,
                  !panelClass
                    ? classes.flightInfoSecondaryExtraInfoDownSmHide
                    : classes.flightInfoSecondaryExtraInfoDownSmHidePanel,
                )}>
                <p>
                  {numberOfStops}
                  {countStopsLabel}
                  &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                  {airlines &&
                    airlines.indexOf(',') === -1 &&
                    !isChangeFlights &&
                    getDictionaryValue(Dictionary.airOperatorLabel.key)}{' '}
                  {airlines}
                  &nbsp;&nbsp;&nbsp;{!isChangeFlights ? '|' : '-'}
                  &nbsp;&nbsp;&nbsp;
                  {cabinClassNames[flightClass]}
                </p>
                {!isChange && (
                  <>
                    <p>|</p>
                    <FareTooltip elementLabel={refundableLabel} />
                  </>
                )}
              </div>
              <div
                className={clsx(
                  classes.flightInfoSecondaryExtraInfo,
                  !panelClass
                    ? classes.flightInfoSecondaryExtraInfoDownSmShow
                    : classes.flightInfoSecondaryExtraInfoDownSmShowPanel,
                )}>
                <div>
                  {numberOfStops}
                  {countStopsLabel}
                </div>
                <div className={clsx(classes.justifiedFlightInfo, classes.flightInfoOperatedBy)}>
                  <p>
                    {airlines &&
                      airlines.indexOf(',') === -1 &&
                      !isChangeFlights &&
                      getDictionaryValue(Dictionary.airOperatorLabel.key)}{' '}
                    {airlines}
                    &nbsp;&nbsp;&nbsp;{!isChangeFlights ? '|' : '-'}
                    &nbsp;&nbsp;&nbsp;
                    {cabinClassNames[flightClass]}
                  </p>
                  {!isChange && (
                    <>
                      <p>|</p>
                      <FareTooltip elementLabel={refundableLabel} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className={clsx(
              classes.flightInfoWrapper,
              classes.secondColumn,
              !panelClass ? classes.flightInfoDownLgHide : classes.flightInfoDownLgHidePanel,
            )}>
            <div className={classes.flightInfoPrimary}>{totalDuration}</div>
            <div className={classes.flightInfoSecondary}>{`${departureCity} - ${arrivalCity}`}</div>
          </div>
          <div
            className={clsx(
              classes.flightInfoWrapper,
              classes.secondColumn,
              !panelClass ? classes.flightInfoDownLgHide : classes.flightInfoDownLgHidePanel,
            )}>
            <div className={classes.flightInfoPrimary}>{numberOfStops}</div>
            <div className={classes.flightInfoSecondary}>{countStopsLabelBig}</div>
          </div>
          <div
            className={clsx(
              classes.flightInfoWrapper,
              classes.textAlignRight,
              classes.fourthColumn,
            )}>
            <div className={classes.flightInfoPrimary}>{flightNumber}</div>
            <div className={classes.flightInfoSecondary}>
              {numberOfPassangers}{' '}
              {numberOfPassangers === 1
                ? getDictionaryValue(Dictionary.airPassengerLabel.key)
                : getDictionaryValue(Dictionary.airPassengersLabel.key)}
            </div>
          </div>
        </div>
        {airlines === 'American Airlines' && isSummaryPage && (
          <div>
            <p className={classes.aaLoyaltyMessage}>
              {getDictionaryValue(Dictionary.airAALoyaltyMessage?.key)}
            </p>
          </div>
        )}
      </div>
      <FlightDetailsModal
        openModal={openFlightDetailsModal}
        handleClose={handleCloseFlightDetailsModal}
        flightInfo={selectedFlight}
      />
    </>
  );
};

export default ReviewInfo;
